import { useState } from "react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Send, MessageSquareHeart } from "lucide-react";

const schema = z.object({
  name: z.string().trim().max(100).optional().or(z.literal("")),
  email: z.string().trim().email("Please enter a valid email").max(255).optional().or(z.literal("")),
  message: z.string().trim().min(1, "Message cannot be empty").max(2000, "Message too long"),
});

export default function FeedbackForm() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const parsed = schema.safeParse({ name, email, message });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? "Invalid input");
      return;
    }

    // Client-side throttle: 30s between sends, max 5 per hour, max 10 per day
    if (typeof window !== "undefined") {
      const COOLDOWN_MS = 30_000;
      const HOUR_MS = 60 * 60 * 1000;
      const DAY_MS = 24 * HOUR_MS;
      const now = Date.now();
      const raw = localStorage.getItem("hvi_feedback_history");
      const history: number[] = raw ? JSON.parse(raw).filter((t: number) => now - t < DAY_MS) : [];
      const last = history[history.length - 1] ?? 0;
      const lastHour = history.filter((t) => now - t < HOUR_MS).length;

      if (now - last < COOLDOWN_MS) {
        const wait = Math.ceil((COOLDOWN_MS - (now - last)) / 1000);
        toast.error(`Please wait ${wait}s before sending another message.`);
        return;
      }
      if (lastHour >= 5) {
        toast.error("You've sent too many messages this hour. Try again later.");
        return;
      }
      if (history.length >= 10) {
        toast.error("Daily message limit reached. Please try again tomorrow.");
        return;
      }
    }

    setSubmitting(true);
    const { error } = await supabase.from("feedback").insert({
      name: parsed.data.name || null,
      email: parsed.data.email || null,
      message: parsed.data.message,
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    });
    setSubmitting(false);
    if (error) {
      const msg = /too many|temporarily unavailable/i.test(error.message)
        ? error.message
        : "Could not send your message. Please try again.";
      toast.error(msg);
      return;
    }

    if (typeof window !== "undefined") {
      const raw = localStorage.getItem("hvi_feedback_history");
      const history: number[] = raw ? JSON.parse(raw) : [];
      history.push(Date.now());
      localStorage.setItem("hvi_feedback_history", JSON.stringify(history.slice(-20)));
    }

    setDone(true);
    setName("");
    setEmail("");
    setMessage("");
    toast.success("Thanks — your message was received.");
  }

  return (
    <section
      id="feedback"
      className="relative w-full border-t border-border bg-gradient-to-b from-background via-background to-hvi-primary/5 px-4 py-14 md:py-20 scroll-mt-20"
    >
      {/* subtle background glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 overflow-hidden"
      >
        <div className="absolute left-1/2 top-0 h-64 w-[600px] -translate-x-1/2 rounded-full bg-hvi-primary/10 blur-3xl" />
      </div>

      <div className="relative mx-auto w-full max-w-2xl">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-hvi-primary/15 text-hvi-primary ring-1 ring-hvi-primary/30">
            <MessageSquareHeart className="h-6 w-6" />
          </div>
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
            Share your thoughts
          </h2>
          <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
            Spotted an error, have a tip, or want to leave feedback?
            We read every message.
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          className="rounded-3xl border border-border bg-card/80 p-5 md:p-7 shadow-lg shadow-black/5 backdrop-blur space-y-4"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input
              type="text"
              placeholder="Name (optional)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
              className="rounded-2xl border border-border bg-background px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-hvi-primary/40 transition"
            />
            <input
              type="email"
              placeholder="Email (optional)"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              maxLength={255}
              className="rounded-2xl border border-border bg-background px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-hvi-primary/40 transition"
            />
          </div>
          <textarea
            placeholder="Your message…"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            required
            maxLength={2000}
            rows={6}
            className="w-full rounded-2xl border border-border bg-background px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-hvi-primary/40 resize-y transition"
          />
          <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
            <span className="text-[11px] text-muted-foreground text-center sm:text-left">
              {message.length}/2000 · We never share your email.
            </span>
            <button
              type="submit"
              disabled={submitting || message.trim().length === 0}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-hvi-primary px-6 py-3 text-sm font-semibold text-primary-foreground shadow-md shadow-hvi-primary/30 transition hover:opacity-90 hover:shadow-lg active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="h-4 w-4" />
              {submitting ? "Sending…" : done ? "Sent ✓" : "Send message"}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
